import { useState } from "react";
import { toast } from "sonner";
import { Inbox, MailOpen, Mail } from "lucide-react";
import { api, errMsg } from "../../lib/api";

export default function EnquiryInbox({ enquiries, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const unread = enquiries.filter((e) => !e.read).length;

  const markRead = async (e) => {
    if (busyId) return;
    setBusyId(e.id);
    try {
      await api.post(`/enquiries/${e.id}/read`);
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section data-testid="enquiry-inbox" className="border border-line bg-panel p-6 md:p-8">
      <div className="flex items-center gap-3">
        <Inbox size={14} className="text-ochre" />
        <p className="eyebrow !text-ochre/90">Messages</p>
      </div>
      <div className="flex items-end justify-between gap-4 flex-wrap mt-2">
        <h2 className="font-display text-2xl">Enquiry Inbox</h2>
        <p className="font-meta text-[10px] uppercase tracking-[0.25em] text-mute" data-testid="enquiry-unread-count">
          {unread > 0 ? `${String(unread).padStart(2, "0")} new` : "All read"}
        </p>
      </div>

      {enquiries.length === 0 ? (
        <p className="font-meta text-[9px] uppercase tracking-[0.25em] text-mute mt-6">
          No messages yet — collector enquiries from your artworks land here and in your inbox.
        </p>
      ) : (
        <ul className="mt-6 space-y-4">
          {enquiries.map((e) => (
            <li
              key={e.id}
              data-testid={`enquiry-${e.id}`}
              className={`border border-line bg-elevated p-4 ${e.read ? "" : "border-l-2 border-l-ochre"}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="font-display text-lg leading-tight">
                    {e.name} <span className="text-mute text-sm">about</span> <span className="italic">{e.artwork_title}</span>
                  </p>
                  <a
                    href={`mailto:${e.email}`}
                    data-testid={`enquiry-email-${e.id}`}
                    className="font-meta text-[10px] uppercase tracking-[0.2em] text-ochre hover:text-gold transition-colors"
                  >
                    {e.email}
                  </a>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-meta text-[9px] uppercase tracking-[0.2em] text-mute">
                    {new Date(e.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                  {e.read ? (
                    <span className="flex items-center gap-1.5 font-meta text-[9px] uppercase tracking-[0.2em] text-mute" data-testid={`enquiry-read-${e.id}`}>
                      <MailOpen size={11} /> Read
                    </span>
                  ) : (
                    <button
                      onClick={() => markRead(e)}
                      disabled={busyId === e.id}
                      data-testid={`enquiry-read-button-${e.id}`}
                      className="flex items-center gap-1.5 border border-ochre/50 px-3 py-1.5 font-meta text-[9px] uppercase tracking-[0.2em] text-ochre hover:bg-ochre hover:text-ink transition-colors duration-300 disabled:opacity-40"
                    >
                      <Mail size={10} /> Mark read
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm leading-relaxed text-bone/85 mt-3">{e.message}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
